/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.14.0
 */

import React, { FC, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import invoke from 'lodash/invoke';

import { KeyboardKeyCodeEnum } from '../../../modules/app/appConstants';
import { TooltipModalStyled } from './styled/TooltipModalStyled';
import { ITooltipModalComposition, ModalCompoundBody } from './TooltipModalCompounds';
import TooltipModalContext from './TooltipModalContext';

export interface ITooltipModalChildren {
    onClose: () => void;
    onToggle: () => void;
}

export interface ITooltipModal {
    children: (props: ITooltipModalChildren) => ReactNode;
}

const TooltipModal: FC<ITooltipModal> & ITooltipModalComposition = ({ children }) => {

    const [isTooltipShown, setTooltipShow] = useState<boolean>(false);

    const tooltipRef: RefObject<HTMLDivElement> = useRef(null);

    const closeTooltip = useCallback((): void => {
        setTooltipShow(false);
    }, []);

    const handleClickOutside = useCallback((event: MouseEvent): void => {
        if (!tooltipRef.current) return;

        if (!invoke(tooltipRef, 'current.contains', event.target as Node)) {
            event.stopPropagation();
            closeTooltip();
        }
    }, [closeTooltip]);

    const keyDownHandler = useCallback((event: React.KeyboardEvent) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            event.stopPropagation();
        } else if (event.keyCode === KeyboardKeyCodeEnum.ESCAPE) {
            event.stopPropagation();
            closeTooltip();
        }
    }, [closeTooltip]);

    useEffect(() => {
        isTooltipShown
            ? window.addEventListener('click', handleClickOutside, true)
            : window.removeEventListener('click', handleClickOutside, true);
        return () => {
            window.removeEventListener('click', handleClickOutside, true);
        };
    }, [isTooltipShown, handleClickOutside]);


    return (
        <TooltipModalStyled ref={tooltipRef} onKeyDown={keyDownHandler}>
            <TooltipModalContext.Provider value={{ isTooltipShown }}>
                {children({
                    onClose: closeTooltip,
                    onToggle: () => setTooltipShow(!isTooltipShown),
                })}
            </TooltipModalContext.Provider>
        </TooltipModalStyled>
    );
};

TooltipModal.Body = ModalCompoundBody;

export default TooltipModal;
